// Web-build report delivery. The desktop app writes a report to a user-chosen
// path and opens it locally; a browser can do neither, so the webserver
// generates the report and streams it back as an attachment, and we save it to
// the browser's Downloads. On a non-OK response this throws the server's error
// text so callers can surface it the way the desktop surfaces a command
// rejection. All DOM/global access lives here so call sites stay free of the
// `window` parameter shadowing some report functions have.
import { getTigerResource, tigerFormat } from "./tigerResource";

export interface DownloadedReport {
  /** The name the file was actually saved under. */
  filename: string;
  /** Set when the report could not be converted to the requested format and
   *  arrived in the one it was generated in: a message to show the user. */
  warning?: string;
}

export async function downloadReport(
  url: string,
  filename: string,
  init?: RequestInit,
): Promise<DownloadedReport> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      text ||
        tigerFormat(getTigerResource("TIGER_REPORT_REQUEST_FAILED"), String(res.status)),
    );
  }
  // A .pdf/.docx report is the generated document (.doc or flat ODF) run
  // through a converter, which needs Word / LibreOffice / the convert server.
  // When that step fails the webserver sends the unconverted document rather
  // than throwing the whole report away — and then the format it really is,
  // not the one we asked for, is what this has to be saved as.
  const servedFormat = res.headers.get("X-Tiger-Report-Format")?.trim();
  const convertError = res.headers.get("X-Tiger-Convert-Error")?.trim();
  const saveAs = servedFormat ? withExtension(filename, servedFormat) : filename;
  const warning = convertError
    ? tigerFormat(
        getTigerResource("TIGER_REPORT_CONVERT_FAILED_ORIGINAL"),
        extensionOf(filename),
        saveAs,
        convertError,
      )
    : undefined;

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = saveAs;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
  return { filename: saveAs, warning };
}

/** The requested format, for the "could not be converted to .pdf" message. */
function extensionOf(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(i + 1) : "";
}

/** The same name under the format the server actually sent. */
function withExtension(filename: string, ext: string): string {
  const i = filename.lastIndexOf(".");
  return `${i > 0 ? filename.slice(0, i) : filename}.${ext}`;
}
